import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { toast } from '@/hooks/use-toast'
import { useUpdateOrganization } from '@/features/admin/organizationsQueries'
import { useIsRootOrganization } from '@/features/admin/useIsRootOrganization'
import type { AdminOrganizationDetail } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['standard', 'partner']),
  tenant_id: z.string().regex(uuid, 'Must be a UUID'),
})

type FormValues = z.infer<typeof schema>

interface Props {
  organization?: AdminOrganizationDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditOrganizationDialog({ organization, open, onOpenChange }: Props) {
  const { isRoot } = useIsRootOrganization()
  const update = useUpdateOrganization(organization?.id ?? '')
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', type: 'standard', tenant_id: '' },
  })

  useEffect(() => {
    if (open && organization) {
      reset({
        name: organization.name,
        type: organization.type,
        tenant_id: organization.tenant_id,
      })
    }
  }, [open, organization, reset])

  const tenantChanged = !!organization && watch('tenant_id') !== organization.tenant_id

  const onSubmit = handleSubmit(async (values) => {
    try {
      // Type and tenant are only sent by root; the API rejects them from
      // anyone else, so there is nothing to reject.
      await update.mutateAsync(isRoot ? values : { name: values.name })
      toast({ title: 'Organization saved' })
      onOpenChange(false)
    } catch (error) {
      toast({
        title: 'Could not save',
        description: (error as ApiRequestError)?.message,
        variant: 'destructive',
      })
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isRoot ? 'Edit organization' : 'Rename organization'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Name</label>
            <Input {...register('name')} />
            {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Type</label>
            {isRoot ? (
              <Select
                value={watch('type')}
                onValueChange={(v) => setValue('type', v as FormValues['type'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input value={organization?.type === 'partner' ? 'Partner' : 'Standard'} disabled />
            )}
          </div>

          {isRoot && (
            <p className="text-xs text-gray-400">
              Partner is what gives an organization access to this tool for its own subtree. A
              partner that administers organizations cannot be made standard.
            </p>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Tenant ID</label>
            {isRoot ? (
              <>
                <Input {...register('tenant_id')} spellCheck={false} className="font-mono text-xs" />
                {errors.tenant_id && (
                  <p className="text-xs text-red-600">{errors.tenant_id.message}</p>
                )}
                {tenantChanged && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    This moves the organization to a different Microsoft tenant. Everyone signing in
                    from the current tenant loses access, and the new tenant's users gain it.
                  </p>
                )}
              </>
            ) : (
              <Input value={organization?.tenant_id ?? ''} disabled className="font-mono text-xs" />
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
